import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { ElMessage } from 'element-plus'
import { BASE_URL } from './baseUrl'
import { trackApiOperation } from '@/utils/learningTracker'
import router from '@/router'
import { redirectToLogin } from '@/utils/authNavigation'

export class AuthRedirectError extends Error {
  constructor() {
    super('Authentication required')
    this.name = 'AuthRedirectError'
  }
}

export function isAuthRedirectError(error: unknown): error is AuthRedirectError {
  return error instanceof AuthRedirectError
}

export interface RequestConfig extends AxiosRequestConfig {
  _retry?: boolean
  suppressErrorMessage?: boolean
  metadata?: {
    startTime: number
  }
}

function getErrorMessage(data: any, fallback: string): string {
  if (typeof data === 'string' && data.trim()) {
    return data
  }

  if (typeof data?.detail === 'string' && data.detail.trim()) {
    return data.detail
  }

  if (typeof data?.message === 'string' && data.message.trim()) {
    return data.message
  }

  return fallback
}

function isLoginRequest(requestConfig: RequestConfig) {
  return String(requestConfig.url || '').includes('/auth/login')
}

class Request {
  private instance: AxiosInstance

  constructor() {
    this.instance = axios.create({
      baseURL: BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    this.instance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        ;(config as RequestConfig).metadata = { startTime: Date.now() }
        return config
      },
      (error) => Promise.reject(error)
    )

    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        const requestConfig = response.config as RequestConfig
        trackApiOperation({
          method: response.config.method,
          url: response.config.url,
          statusCode: response.status,
          durationMs: requestConfig.metadata ? Date.now() - requestConfig.metadata.startTime : undefined,
          success: true,
        })
        return response.data
      },
      (error) => {
        const requestConfig = (error.config || {}) as RequestConfig
        trackApiOperation({
          method: requestConfig.method,
          url: requestConfig.url,
          statusCode: error.response?.status,
          durationMs: requestConfig.metadata ? Date.now() - requestConfig.metadata.startTime : undefined,
          success: false,
        })

        if (error.response) {
          const { status, data } = error.response
          const shouldNotify = !requestConfig.suppressErrorMessage
          
          switch (status) {
            case 400:
              if (shouldNotify) {
                ElMessage.error(getErrorMessage(data, '请求参数错误'))
              }
              break
            case 401:
              if (!isLoginRequest(requestConfig)) {
                void redirectToLogin(router, router.currentRoute.value.fullPath)
                return Promise.reject(new AuthRedirectError())
              }
              if (shouldNotify) {
                ElMessage.error(getErrorMessage(data, '未授权'))
              }
              break
            case 403:
              if (shouldNotify) {
                ElMessage.error('拒绝访问')
              }
              break
            case 404:
              if (shouldNotify) {
                ElMessage.error('请求资源不存在')
              }
              break
            case 500:
              if (shouldNotify) {
                ElMessage.error('服务器内部错误')
              }
              break
            default:
              if (shouldNotify) {
                ElMessage.error(getErrorMessage(data, '请求失败'))
              }
          }
        } else {
          if (!requestConfig.suppressErrorMessage) {
            if (error.code === 'ECONNABORTED' || String(error.message || '').toLowerCase().includes('timeout')) {
              ElMessage.error('请求超时，请稍后重试')
            } else {
              ElMessage.error('网络错误，请检查网络连接')
            }
          }
        }
        return Promise.reject(error)
      }
    )
  }

  public request<T = any>(config: RequestConfig): Promise<T> {
    return this.instance.request(config)
  }

  public get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request({ ...config, url, method: 'GET' })
  }

  public post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request({ ...config, url, method: 'POST', data })
  }

  public put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request({ ...config, url, method: 'PUT', data })
  }

  public patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request({ ...config, url, method: 'PATCH', data })
  }

  public delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request({ ...config, url, method: 'DELETE' })
  }
}

export const request = new Request()
export default request
